import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Song, Playlist, Whitelist, RepeatMode } from '@/types';

interface AppStore {
  // Whitelist
  whitelist: Whitelist | null;
  whitelistedIds: Set<string>;
  setWhitelist: (whitelist: Whitelist) => void;

  // Navigation
  currentTab: number;
  setCurrentTab: (tab: number) => void;

  // Playback
  queue: Song[];
  currentTrack: Song | null;
  trackIndex: number;
  isPlaying: boolean;
  repeatMode: RepeatMode;
  shuffleMode: boolean;
  volume: number;

  setQueue: (queue: Song[], startIndex?: number) => void;
  addToQueue: (song: Song) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  setCurrentTrack: (track: Song | null, index: number) => void;
  setIsPlaying: (playing: boolean) => void;
  setRepeatMode: (mode: RepeatMode) => void;
  toggleShuffle: () => void;
  setVolume: (volume: number) => void;
  next: () => Song | null;
  previous: () => Song | null;

  // Library
  favorites: Set<string>;
  favoriteSongs: Song[];
  playlists: Playlist[];
  recentlyPlayed: Song[];
  addFavorite: (song: Song) => void;
  removeFavorite: (videoId: string) => void;
  isFavorite: (videoId: string) => boolean;
  addPlaylist: (playlist: Playlist) => void;
  updatePlaylist: (playlist: Playlist) => void;
  deletePlaylist: (id: string) => void;
  addToRecentlyPlayed: (song: Song) => void;

  // Search
  searchHistory: string[];
  addToSearchHistory: (query: string) => void;
  clearSearchHistory: () => void;

  // Theme
  theme: 'light' | 'dark' | 'auto';
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
}

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      whitelist: null,
      whitelistedIds: new Set(),
      currentTab: 0,
      queue: [],
      currentTrack: null,
      trackIndex: -1,
      isPlaying: false,
      repeatMode: 'off',
      shuffleMode: false,
      volume: 1,
      favorites: new Set(),
      favoriteSongs: [],
      playlists: [],
      recentlyPlayed: [],
      searchHistory: [],
      theme: 'dark',

      // Whitelist actions
      setWhitelist: (whitelist) => {
        const ids = new Set<string>();
        whitelist.artists.forEach((artist) => {
          const id = artist.link || artist.id;
          if (id) ids.add(id);
        });
        set({ whitelist, whitelistedIds: ids });
      },

      // Navigation actions
      setCurrentTab: (tab) => set({ currentTab: tab }),

      // Playback actions
      setQueue: (queue, startIndex = 0) => {
        set({
          queue,
          trackIndex: startIndex,
          currentTrack: queue[startIndex] || null,
        });
      },

      addToQueue: (song) => {
        set((state) => ({ queue: [...state.queue, song] }));
      },

      removeFromQueue: (index) => {
        set((state) => {
          const newQueue = state.queue.filter((_, i) => i !== index);
          let newIndex = state.trackIndex;

          if (index < state.trackIndex) {
            newIndex--;
          } else if (index === state.trackIndex) {
            newIndex = Math.min(newIndex, newQueue.length - 1);
          }

          return {
            queue: newQueue,
            trackIndex: newIndex,
            currentTrack: newQueue[newIndex] || null,
          };
        });
      },

      clearQueue: () => {
        set({ queue: [], currentTrack: null, trackIndex: -1, isPlaying: false });
      },

      setCurrentTrack: (track, index) => {
        set({ currentTrack: track, trackIndex: index });
        if (track) {
          get().addToRecentlyPlayed(track);
        }
      },

      setIsPlaying: (playing) => set({ isPlaying: playing }),

      setRepeatMode: (mode) => set({ repeatMode: mode }),

      toggleShuffle: () => {
        set((state) => {
          const shuffleMode = !state.shuffleMode;

          if (shuffleMode) {
            // Shuffle remaining tracks
            const remaining = state.queue.slice(state.trackIndex + 1);
            const shuffled = [...remaining].sort(() => Math.random() - 0.5);
            const newQueue = [...state.queue.slice(0, state.trackIndex + 1), ...shuffled];
            return { shuffleMode, queue: newQueue };
          }

          return { shuffleMode };
        });
      },

      setVolume: (volume) => set({ volume: Math.max(0, Math.min(1, volume)) }),

      next: () => {
        const state = get();
        const { queue, trackIndex, repeatMode } = state;

        // Repeat one
        if (repeatMode === 'one') {
          return state.currentTrack;
        }

        // Normal next
        if (trackIndex < queue.length - 1) {
          const nextIndex = trackIndex + 1;
          const nextTrack = queue[nextIndex];
          set({ trackIndex: nextIndex, currentTrack: nextTrack });
          if (nextTrack) {
            state.addToRecentlyPlayed(nextTrack);
          }
          return nextTrack || null;
        }

        // Repeat all
        if (repeatMode === 'all' && queue.length > 0) {
          const nextTrack = queue[0];
          set({ trackIndex: 0, currentTrack: nextTrack });
          if (nextTrack) {
            state.addToRecentlyPlayed(nextTrack);
          }
          return nextTrack || null;
        }

        return null;
      },

      previous: () => {
        const state = get();
        const { queue, trackIndex } = state;

        if (trackIndex > 0) {
          const prevIndex = trackIndex - 1;
          const prevTrack = queue[prevIndex];
          set({ trackIndex: prevIndex, currentTrack: prevTrack });
          return prevTrack || null;
        }

        return null;
      },

      // Library actions
      addFavorite: (song) => {
        set((state) => {
          if (state.favorites.has(song.videoId)) {
            return state; // Already favorited
          }
          const favorites = new Set(state.favorites);
          favorites.add(song.videoId);
          const favoriteSongs = [song, ...state.favoriteSongs];
          return { favorites, favoriteSongs };
        });
      },

      removeFavorite: (videoId) => {
        set((state) => {
          const favorites = new Set(state.favorites);
          favorites.delete(videoId);
          const favoriteSongs = state.favoriteSongs.filter(s => s.videoId !== videoId);
          return { favorites, favoriteSongs };
        });
      },

      isFavorite: (videoId) => {
        return get().favorites.has(videoId);
      },

      addPlaylist: (playlist) => {
        set((state) => ({ playlists: [...state.playlists, playlist] }));
      },

      updatePlaylist: (playlist) => {
        set((state) => ({
          playlists: state.playlists.map((p) => (p.id === playlist.id ? playlist : p)),
        }));
      },

      deletePlaylist: (id) => {
        set((state) => ({
          playlists: state.playlists.filter((p) => p.id !== id),
        }));
      },

      addToRecentlyPlayed: (song) => {
        set((state) => {
          const filtered = state.recentlyPlayed.filter((s) => s.videoId !== song.videoId);
          const updated = [song, ...filtered].slice(0, 50);
          return { recentlyPlayed: updated };
        });
      },

      // Search actions
      addToSearchHistory: (query) => {
        const trimmed = query.trim();
        if (!trimmed) return;

        set((state) => {
          const filtered = state.searchHistory.filter((q) => q !== trimmed);
          const updated = [trimmed, ...filtered].slice(0, 20);
          return { searchHistory: updated };
        });
      },

      clearSearchHistory: () => set({ searchHistory: [] }),

      // Theme actions
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'zemer-music-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        favorites: Array.from(state.favorites),
        favoriteSongs: state.favoriteSongs,
        playlists: state.playlists,
        recentlyPlayed: state.recentlyPlayed.slice(0, 20),
        searchHistory: state.searchHistory,
        theme: state.theme,
        volume: state.volume,
        repeatMode: state.repeatMode,
        shuffleMode: state.shuffleMode,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            // Convert favorites array back to Set
            state.favorites = new Set(state.favorites as unknown as string[]);
            // Ensure favoriteSongs is an array
            state.favoriteSongs = state.favoriteSongs || [];
          }
        };
      },
    }
  )
);
